import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { prepareImageForUpload } from '../lib/imageUpload'
import { useAuth } from '../context/AuthContext'
import Header from '../components/Header'
import FoundingListerBadge from '../components/FoundingListerBadge'
import { ACCOUNT_DELETION_REASONS } from '../lib/removalReasons'

export default function Profile() {
const { user, profile, refreshProfile, signOut } = useAuth()
const navigate = useNavigate()
const [fullName, setFullName] = useState('')
const [suburb, setSuburb] = useState('')
const [phone, setPhone] = useState('')
const [avatarUrl, setAvatarUrl] = useState('')
const [uploading, setUploading] = useState(false)
const [saving, setSaving] = useState(false)
const [message, setMessage] = useState('')
const [error, setError] = useState('')
const [deleting, setDeleting] = useState(false)
const [showDeleteModal, setShowDeleteModal] = useState(false)
const [deleteError, setDeleteError] = useState('')
const [showBlockedModal, setShowBlockedModal] = useState(false)
const [blockedCount, setBlockedCount] = useState(0)
const [checkingDeletable, setCheckingDeletable] = useState(false)
const [deleteConfirmText, setDeleteConfirmText] = useState('')
const [deleteReason, setDeleteReason] = useState('')
const [reviews, setReviews] = useState([])
const fileRef = useRef()

useEffect(() => {
if (!user) return // ProtectedRoute sends logged-out visitors to sign-in
if (profile) {
setFullName(profile.full_name || '')
setSuburb(profile.suburb || '')
setPhone(profile.phone || '')
setAvatarUrl(profile.avatar_url || '')
}
if (user) {
// Load reviews about me and mark the badge as seen.
// Fire-and-forget: the mark-seen update doesn't need to await — failure just leaves the badge up
// until the next visit; nothing in the page depends on it.
supabase
.from('reviews')
.select('*, reviewer:profiles!reviews_reviewer_id_fkey(full_name, avatar_url)')
.eq('reviewee_id', user.id)
.order('created_at', { ascending: false })
.limit(10)
.then(({ data }) => setReviews(data || []))
supabase.from('profiles').update({ last_seen_reviews_at: new Date().toISOString() }).eq('id', user.id)
}
}, [user, profile])

const uploadAvatar = async (e) => {
const file = e.target.files?.[0]
if (!file) return
setUploading(true)
setError('')
let fileToUpload
try {
fileToUpload = await prepareImageForUpload(file)
} catch (prepErr) {
console.error('Avatar prep failed:', prepErr)
setError('Could not process this image. Try a JPEG or PNG instead.')
setUploading(false)
return
}
const fileName = `${user.id}-${Date.now()}.jpg`
const { error: uploadError } = await supabase.storage
.from('profile-images')
.upload(fileName, fileToUpload, { cacheControl: '31536000', upsert: true, contentType: 'image/jpeg' })
if (uploadError) { setError(uploadError.message); setUploading(false); return }
const { data } = supabase.storage.from('profile-images').getPublicUrl(fileName)
setAvatarUrl(data.publicUrl)
await supabase.from('profiles').update({ avatar_url: data.publicUrl }).eq('id', user.id)
if (refreshProfile) refreshProfile()
setUploading(false)
setMessage('Profile photo updated!')
}

const saveProfile = async () => {
setSaving(true)
setError('')
const { error } = await supabase.from('profiles').update({
full_name: fullName,
suburb,
phone,
avatar_url: avatarUrl,
}).eq('id', user.id)
if (error) { setError(error.message); setSaving(false); return }
if (refreshProfile) refreshProfile()
setMessage('Profile saved!')
setSaving(false)
}

const countNonDeletableBookings = async () => {
const { count, error } = await supabase
.from('bookings')
.select('id', { count: 'exact', head: true })
.or(`hirer_id.eq.${user.id},owner_id.eq.${user.id}`)
.not('status', 'in', '(enquiry,pending,cancelled)')
return { count: count || 0, error }
}

const handleDeleteClick = async () => {
setDeleteError('')
setCheckingDeletable(true)
const { count, error } = await countNonDeletableBookings()
setCheckingDeletable(false)
if (error) {
setDeleteError('Could not check your bookings. Please try again.')
return
}
if (count > 0) {
setBlockedCount(count)
setShowBlockedModal(true)
return
}
setDeleteConfirmText('')
setDeleteReason('')
setShowDeleteModal(true)
}

const deleteAccount = async () => {
setDeleting(true)
setDeleteError('')

// Defensive re-check: bookings may have changed since the modal opened,
// and we never want self-service deletion to run when any non-deletable
// booking exists. This is the second of two guards (the first runs on click).
const { count: blockingCount, error: blockedErr } = await countNonDeletableBookings()
if (blockedErr) {
setDeleteError('Could not verify your bookings. Please try again.')
setDeleting(false)
return
}
if (blockingCount > 0) {
setDeleteError('You have active or completed hires that prevent self-service deletion. Please contact hello@hireitnow.au.')
setDeleting(false)
return
}

const FAIL = 'Something went wrong deleting your account — please contact hello@hireitnow.au. Your account has not been closed.'

// The backend deletes everything in one database transaction (all-or-nothing),
// logs an anonymous record, then removes the user's photos from storage
try {
const { data: { session } } = await supabase.auth.getSession()
const res = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'https://hireit-production-ac0f.up.railway.app'}/delete-account`, {
method: 'POST',
headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
body: JSON.stringify({ reason: deleteReason || null }),
})
if (res.status === 409) {
setDeleteError('You have active or completed hires that prevent self-service deletion. Please contact hello@hireitnow.au.')
setDeleting(false)
return
}
if (!res.ok) { setDeleteError(FAIL); setDeleting(false); return }
} catch {
setDeleteError(FAIL); setDeleting(false); return
}

await signOut()
navigate('/')
}

const initials = fullName
? fullName.split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase()
: user?.email?.[0]?.toUpperCase() || '?'

return (
<div>
<Header />
<div className="container" style={{ maxWidth: 500, padding: '1.5rem 1rem' }}>
<div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1.5rem' }}>
<button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)}>← Back</button>
<h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 0 }}>My profile</h1>
</div>

{message && <div className="alert alert-success" style={{ marginBottom: 16 }}>{message}</div>}
{error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}

<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '2rem' }}>
<div
onClick={() => fileRef.current?.click()}
style={{ width: 100, height: 100, borderRadius: '50%', background: avatarUrl ? 'transparent' : 'rgba(45, 63, 204, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', overflow: 'hidden', border: '3px solid var(--primary)', position: 'relative' }}
>
{avatarUrl ? (
<img src={avatarUrl} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
) : (
<span style={{ fontSize: 36, fontWeight: 600, color: 'var(--primary)' }}>{initials}</span>
)}
<div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.5)', color: 'white', fontSize: 11, textAlign: 'center', padding: '4px 0' }}>
{uploading ? 'Uploading...' : 'Change'}
</div>
</div>
<input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={uploadAvatar} />
<p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>Tap to upload a profile photo</p>
</div>

{profile?.founding_lister_number && (
<div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
<FoundingListerBadge profile={profile} size="lg" />
</div>
)}

<div className="card card-body" style={{ marginBottom: '1rem' }}>
<div className="form-group">
<label className="form-label">Full name</label>
<input className="form-control" value={fullName} onChange={e => setFullName(e.target.value)} placeholder="Your full name" />
</div>
<div className="form-group">
<label className="form-label">Suburb</label>
<input className="form-control" value={suburb} onChange={e => setSuburb(e.target.value)} placeholder="Your suburb" />
</div>
<div className="form-group">
<label className="form-label">Phone number</label>
<input className="form-control" value={phone} onChange={e => setPhone(e.target.value)} placeholder="Your phone number" />
</div>
<button className="btn btn-primary btn-full" onClick={saveProfile} disabled={saving}>
{saving ? 'Saving...' : 'Save profile'}
</button>
</div>

<div className="card card-body" style={{ marginBottom: '1rem' }}>
<div style={{ fontWeight: 500, marginBottom: 4 }}>Account</div>
<div style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 12 }}>{user?.email}</div>
<div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
{((profile?.review_count || 0) > 0 && profile?.rating != null && profile?.rating > 0) ? `⭐ Rating: ${profile.rating.toFixed(1)} (${profile.review_count} reviews)` : 'New — no reviews yet'}
</div>
</div>

<div className="card card-body" style={{ marginBottom: '1rem' }}>
<div style={{ fontWeight: 500, marginBottom: 12 }}>Reviews about you</div>
{reviews.length === 0 ? (
<div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 14, padding: '12px 0' }}>
<div style={{ fontSize: 28, marginBottom: 6 }}>⭐</div>
No reviews yet
</div>
) : (
<div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
{reviews.map(review => {
const reviewerInitials = review.reviewer?.full_name
? review.reviewer.full_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
: '?'
return (
<div key={review.id} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12 }}>
<div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
<div style={{ width: 32, height: 32, borderRadius: '50%', background: review.reviewer?.avatar_url ? 'transparent' : 'var(--primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
{review.reviewer?.avatar_url ? (
<img src={review.reviewer.avatar_url} alt={review.reviewer.full_name} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
) : (
<span style={{ fontSize: 12, fontWeight: 700, color: 'var(--primary)' }}>{reviewerInitials}</span>
)}
</div>
<div style={{ flex: 1 }}>
<div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
<span style={{ fontWeight: 600, fontSize: 13, color: 'var(--navy)' }}>{review.reviewer?.full_name || 'Anonymous'}</span>
<span style={{ fontSize: 12, color: 'var(--amber)' }}>{'⭐'.repeat(review.rating || 5)}</span>
</div>
{review.comment && (
<p style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.5, margin: 0 }}>"{review.comment}"</p>
)}
</div>
</div>
</div>
)
})}
</div>
)}
</div>

<div className="card card-body">
<div style={{ fontWeight: 500, marginBottom: 4, color: 'var(--red)' }}>Danger zone</div>
<p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>
Deleting your account is permanent and cannot be undone.
</p>
<button className="btn btn-danger" onClick={handleDeleteClick} disabled={deleting || checkingDeletable}>
{deleting ? 'Deleting...' : checkingDeletable ? 'Checking...' : 'Delete my account'}
</button>
</div>
</div>

{showDeleteModal && (
<div className="modal-overlay" onClick={() => { setShowDeleteModal(false); setDeleteConfirmText('') }}>
<div className="modal-box" onClick={e => e.stopPropagation()}>
<h3 style={{ fontWeight: 600, marginBottom: 8, color: 'var(--red)' }}>Delete account</h3>
<p style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 16 }}>
Are you sure you want to delete your account? This is permanent and cannot be undone. All your listings, bookings and photos will be removed.
</p>
<p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 6 }}>
Why are you leaving? <span style={{ color: 'var(--text-faint)' }}>(optional)</span>
</p>
<select value={deleteReason} onChange={e => setDeleteReason(e.target.value)} className="form-control" style={{ marginBottom: 16 }}>
<option value="">Prefer not to say</option>
{ACCOUNT_DELETION_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
</select>
<p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 6 }}>
Type <strong>DELETE</strong> to confirm:
</p>
<input
type="text"
value={deleteConfirmText}
onChange={e => setDeleteConfirmText(e.target.value)}
placeholder="DELETE"
style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 14, marginBottom: 16, boxSizing: 'border-box' }}
autoFocus
/>
{deleteError && <div className="alert alert-error" style={{ marginBottom: 12, fontSize: 13 }}>{deleteError}</div>}
<div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
<button className="btn btn-ghost" onClick={() => { setShowDeleteModal(false); setDeleteConfirmText('') }}>Cancel</button>
<button className="btn btn-danger" onClick={deleteAccount} disabled={deleting || deleteConfirmText !== 'DELETE'}>
{deleting ? 'Deleting...' : 'Yes, delete my account'}
</button>
</div>
</div>
</div>
)}

{showBlockedModal && (
<div className="modal-overlay" onClick={() => setShowBlockedModal(false)}>
<div className="modal-box" onClick={e => e.stopPropagation()}>
<h3 style={{ fontWeight: 600, marginBottom: 8, color: 'var(--navy)' }}>We'll handle this one for you</h3>
<p style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 12 }}>
You have {blockedCount} active or completed {blockedCount === 1 ? 'hire' : 'hires'} on your account. We're required to keep the transaction records for those, so deletion needs to be handled manually.
</p>
<p style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 20 }}>
Please email <strong>hello@hireitnow.au</strong> and we'll close your account and Stripe payout account properly for you.
</p>
<div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
<button className="btn btn-primary" onClick={() => setShowBlockedModal(false)}>
OK
</button>
</div>
</div>
</div>
)}
</div>
)
}
